import {
  accountLinkedAuthMethodsRetrieve,
  accountOrgLinkedMethodsRetrieve,
  accountOrgSocialAccountsDestroy,
  accountSocialAccountsDestroy,
  authorizationSocialEntraidConnectCreate,
  authorizationSocialEntraidConnectUrlCreate,
  authorizationSocialExchangeClientCreate,
  authorizationSocialGoogleClientConnectCreate,
  authorizationSocialGoogleConnectCreate,
  authorizationSocialGoogleConnectUrlCreate,
  OrganizationAuthMethodEnum,
  organizationsOrganizationsMemberAuthMethodsList,
  type AccountLinkedAuthMethodsRetrieveResponse,
  type AccountOrgLinkedMethodsRetrieveResponse,
  type OrganizationsOrganizationsMemberAuthMethodsListResponse
} from 'src/api/main'
import { client as mainApiClient } from 'src/api/main/client.gen'
import { paths } from 'src/router'

import { getAuthUrl, getReturnOrigin } from '../utils/authResponseAdapters'

import type { ProviderFlow, ProviderId, SocialProviderId } from './types'

type ConnectUrlParams = {
  provider: SocialProviderId
  flow: Exclude<ProviderFlow, 'login'>
  redirectUri: string
  returnOrigin?: string
  domainName?: string
}

type SocialAuthCallbackParams = {
  provider: SocialProviderId
  flow: ProviderFlow
  code: string
  state: string
  redirectUri: string
  user?: string
}

export const providerService = {
  getCallbackPath(provider: SocialProviderId, flow: ProviderFlow): string {
    if (provider === OrganizationAuthMethodEnum.GOOGLE && flow === 'login') {
      return paths.auth.googleClientCallback
    }
    if (
      provider === OrganizationAuthMethodEnum.GOOGLE &&
      (flow === 'connect' || flow === 'clientConnect')
    ) {
      return paths.auth.googleClientConnectCallback
    }
    if (
      provider === OrganizationAuthMethodEnum.GOOGLE &&
      flow === 'orgConnect'
    ) {
      return paths.auth.googleConnectCallback
    }
    if (provider === OrganizationAuthMethodEnum.ENTRAID && flow === 'connect') {
      return paths.auth.entraIdConnectCallback
    }
    if (
      provider === OrganizationAuthMethodEnum.ENTRAID &&
      flow === 'orgConnect'
    ) {
      return paths.auth.entraIdOrgConnectCallback
    }
    if (provider === OrganizationAuthMethodEnum.APPLE && flow === 'login') {
      return paths.auth.appleClientCallback
    }
    if (
      provider === OrganizationAuthMethodEnum.APPLE &&
      (flow === 'connect' || flow === 'clientConnect')
    ) {
      return paths.auth.appleClientConnectCallback
    }
    if (
      provider === OrganizationAuthMethodEnum.APPLE &&
      flow === 'orgConnect'
    ) {
      return paths.auth.appleOrgConnectCallback
    }
    throw new Error(
      `Unsupported callback path for provider=${provider}, flow=${flow}`
    )
  },

  async getStateInfo(
    provider: SocialProviderId,
    oauthState: string
  ): Promise<string | null> {
    const stateInfoPaths: Record<string, string> = {
      [OrganizationAuthMethodEnum.GOOGLE]:
        '/authorization/social/google/state-info/',
      [OrganizationAuthMethodEnum.ENTRAID]:
        '/authorization/social/entraid/state-info/',
      [OrganizationAuthMethodEnum.APPLE]:
        '/authorization/social/apple/state-info/'
    }
    const stateInfoPath =
      stateInfoPaths[provider] ?? '/authorization/social/google/state-info/'
    const requestUrl = `${stateInfoPath}?state=${encodeURIComponent(oauthState)}`
    const { data } = await mainApiClient.get({ url: requestUrl })

    return getReturnOrigin(data)
  },

  async getConnectUrl(params: ConnectUrlParams): Promise<string> {
    const basePayload = {
      redirect_uri: params.redirectUri,
      ...(params.returnOrigin ? { return_origin: params.returnOrigin } : {})
    }

    if (
      params.provider === OrganizationAuthMethodEnum.ENTRAID &&
      params.flow === 'orgConnect'
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/entraid/org-connect/url/',
        body: {
          ...basePayload,
          domain_name: params.domainName ?? ''
        }
      })
      const authUrl = getAuthUrl(data)
      if (!authUrl) throw new Error('Missing org Entra ID connect auth URL')
      return authUrl
    }

    if (
      params.provider === OrganizationAuthMethodEnum.GOOGLE &&
      params.flow === 'connect'
    ) {
      const { data } = await authorizationSocialGoogleConnectUrlCreate({
        body: { ...basePayload, domain_name: params.domainName ?? '' }
      })
      const authUrl = getAuthUrl(data)
      if (!authUrl) throw new Error('Missing Google connect auth URL')
      return authUrl
    }

    if (
      params.provider === OrganizationAuthMethodEnum.GOOGLE &&
      params.flow === 'orgConnect'
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/google/org-connect/url/',
        body: {
          ...basePayload,
          domain_name: params.domainName ?? ''
        }
      })
      const authUrl = getAuthUrl(data)
      if (!authUrl) throw new Error('Missing org Google connect auth URL')
      return authUrl
    }

    if (
      params.provider === OrganizationAuthMethodEnum.APPLE &&
      params.flow === 'connect'
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/apple/connect/url/',
        body: {
          ...basePayload,
          ...(params.domainName ? { domain_name: params.domainName } : {})
        }
      })
      const authUrl = getAuthUrl(data)
      if (!authUrl) throw new Error('Missing Apple connect auth URL')
      return authUrl
    }

    if (
      params.provider === OrganizationAuthMethodEnum.APPLE &&
      params.flow === 'orgConnect'
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/apple/org-connect/url/',
        body: {
          ...basePayload,
          domain_name: params.domainName ?? ''
        }
      })
      const authUrl = getAuthUrl(data)
      if (!authUrl) throw new Error('Missing org Apple connect auth URL')
      return authUrl
    }

    const { data } = await authorizationSocialEntraidConnectUrlCreate({
      body: {
        ...basePayload,
        ...(params.domainName ? { domain_name: params.domainName } : {})
      }
    })
    const authUrl = getAuthUrl(data)
    if (!authUrl) throw new Error('Missing Entra ID connect auth URL')
    return authUrl
  },

  async processCallback(params: SocialAuthCallbackParams): Promise<unknown> {
    if (
      params.flow === 'login' &&
      params.provider !== OrganizationAuthMethodEnum.GOOGLE &&
      params.provider !== OrganizationAuthMethodEnum.APPLE
    ) {
      throw new Error(`Unsupported login callback provider: ${params.provider}`)
    }

    if (
      params.flow === 'orgConnect' &&
      params.provider !== OrganizationAuthMethodEnum.GOOGLE &&
      params.provider !== OrganizationAuthMethodEnum.ENTRAID &&
      params.provider !== OrganizationAuthMethodEnum.APPLE
    ) {
      throw new Error(`Unsupported org connect provider: ${params.provider}`)
    }

    if (
      params.flow === 'login' &&
      params.provider === OrganizationAuthMethodEnum.GOOGLE
    ) {
      const { data } = await authorizationSocialExchangeClientCreate({
        throwOnError: true,
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (
      params.flow === 'orgConnect' &&
      params.provider === OrganizationAuthMethodEnum.GOOGLE
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/google/org-connect/',
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (
      params.flow === 'orgConnect' &&
      params.provider === OrganizationAuthMethodEnum.ENTRAID
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/entraid/org-connect/',
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (
      params.flow === 'login' &&
      params.provider === OrganizationAuthMethodEnum.APPLE
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/exchange/client/',
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri,
          ...(params.user ? { user: params.user } : {})
        }
      })

      return data
    }

    if (
      params.flow === 'orgConnect' &&
      params.provider === OrganizationAuthMethodEnum.APPLE
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/apple/org-connect/',
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (
      params.provider === OrganizationAuthMethodEnum.APPLE &&
      (params.flow === 'connect' || params.flow === 'clientConnect')
    ) {
      const { data } = await mainApiClient.post({
        throwOnError: true,
        url: '/authorization/social/apple/connect/',
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (
      params.flow === 'clientConnect' &&
      params.provider === OrganizationAuthMethodEnum.GOOGLE
    ) {
      const { data } = await authorizationSocialGoogleClientConnectCreate({
        throwOnError: true,
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (params.provider === OrganizationAuthMethodEnum.GOOGLE) {
      const { data } = await authorizationSocialGoogleConnectCreate({
        throwOnError: true,
        body: {
          provider: params.provider,
          code: params.code,
          state: params.state,
          redirect_uri: params.redirectUri
        }
      })

      return data
    }

    if (params.provider !== OrganizationAuthMethodEnum.ENTRAID) {
      throw new Error(
        `Unsupported connect callback provider/flow: ${params.provider}/${params.flow}`
      )
    }

    const { data } = await authorizationSocialEntraidConnectCreate({
      throwOnError: true,
      body: {
        provider: params.provider,
        code: params.code,
        state: params.state,
        redirect_uri: params.redirectUri
      }
    })

    return data
  },

  unlinkAccountProvider(provider: ProviderId) {
    return accountSocialAccountsDestroy({ path: { provider } })
  },

  unlinkOrgProvider(orgId: number, provider: SocialProviderId) {
    return accountOrgSocialAccountsDestroy({
      path: { org_id: orgId, provider }
    })
  },

  async getLinkedAuthMethods(): Promise<AccountLinkedAuthMethodsRetrieveResponse> {
    const { data } = await accountLinkedAuthMethodsRetrieve()

    if (!data) {
      throw new Error('Missing linked auth methods response')
    }

    return data
  },

  async getOrgLinkedMethods(
    orgId: number
  ): Promise<AccountOrgLinkedMethodsRetrieveResponse> {
    const { data } = await accountOrgLinkedMethodsRetrieve({
      path: { org_id: orgId }
    })

    if (!data) {
      throw new Error('Missing organization linked methods response')
    }

    return data
  },

  async getMemberAuthMethods(
    orgId: number
  ): Promise<OrganizationsOrganizationsMemberAuthMethodsListResponse> {
    const { data } = await organizationsOrganizationsMemberAuthMethodsList({
      path: { id: orgId }
    })

    if (!data) {
      throw new Error('Missing member auth methods response')
    }

    return data
  }
}
